import { useMemo, useState, useCallback, useEffect } from "react";

import { getFieldType } from "../../utils/fieldMapper";
import DynamicField from "./DynamicField";
import {
  isFieldDisabled,
  isFieldRequired,
  isFieldReadOnly,
} from "../../utils/fieldRules";

import { getFieldOptions } from "../../utils/getFieldOptions";
import { applyCalculatedFields } from "../../utils/applyCalculatedFields";
import { sortRowsByConfig } from "../../utils/halper";
import { pasteTableData } from "../../utils/pasteTableData";

const DynamicForm = ({
  columns = [],
  rows = [],
  masterData = {},
  setRows,
  config = {},
  setRowCount,
  rowCount,
}) => {
  const [fontSize, setFontSize] = useState(13);

  const [isRendering, setIsRendering] = useState(false);
  // =====================
  // VISIBLE COLUMNS
  // =====================
  const visibleColumns = useMemo(() => {
    const hiddenFields = config?.fieldRules?.hiddenFields || [];
    const columnOrder = config?.columnOrder || [];

    const filteredColumns = (columns || []).filter(
      (c) => !hiddenFields.includes(c?.Field),
    );

    if (!columnOrder.length) {
      return filteredColumns;
    }

    return [...filteredColumns].sort((a, b) => {
      const indexA = columnOrder.indexOf(a.Field);
      const indexB = columnOrder.indexOf(b.Field);

      const orderA = indexA === -1 ? 9999 : indexA;
      const orderB = indexB === -1 ? 9999 : indexB;

      return orderA - orderB;
    });
  }, [columns, config]);

  const columnMeta = useMemo(() => {
    return visibleColumns.map((column) => ({
      column,
      fieldType: getFieldType(column, config),
      required: isFieldRequired(column?.Field, config),
      readOnly: isFieldReadOnly(column?.Field, config),
    }));
  }, [visibleColumns, config]);

  // =====================
  // HANDLE CHANGE
  // =====================
  const handleChange = useCallback(
    (rowIndex, fieldName, value) => {
      setRows((prev = []) => {
        const updated = [...prev];

        const oldRow = updated[rowIndex] || {};
        const oldValue = oldRow[fieldName];

        let newRow = {
          ...oldRow,
          [fieldName]: value,
        };

        // reset dependent fields
        if (oldValue !== value) {
          Object.entries(config?.dependencies || {}).forEach(
            ([childField, dependency]) => {
              if (dependency?.dependsOn?.includes(fieldName)) {
                newRow[childField] = null;
              }
            },
          );
        }

        // Apply computed fields
        newRow = applyCalculatedFields(newRow, config?.computedFields);

        updated[rowIndex] = newRow;

        return updated;
      });
    },
    [setRows, config],
  );
  // =====================
  // ROW COUNT HANDLER
  // =====================

  const updateRowCount = useCallback(
    (count) => {
      setIsRendering(true);

      requestAnimationFrame(() => {
        setRows((prev = []) => {
          let result;

          const fixedRows = config?.fixedRows;

          // ==========================
          // FIXED ROW MODE
          // ==========================
          if (
            fixedRows?.field &&
            Array.isArray(fixedRows?.values) &&
            fixedRows.values.length
          ) {
            result = fixedRows.values.map((value, index) => ({
              ...prev[index],
              [fixedRows.field]: value,
            }));

            setRowCount(result.length);
          }
          // ==========================
          // NORMAL MODE
          // ==========================
          else {
            setRowCount(count);

            if (count === prev.length) {
              result = prev;
            } else if (count > prev.length) {
              result = [
                ...prev,
                ...Array.from({ length: count - prev.length }, () => ({})),
              ];
            } else {
              result = prev.slice(0, count);
            }
          }

          return result;
        });

        requestAnimationFrame(() => {
          setIsRendering(false);
        });
      });
    },
    [setRows, setRowCount, config],
  );

  const addRow = () => {
    setIsRendering(true);

    requestAnimationFrame(() => {
      setRows((p = []) => [...p, {}]);
      setRowCount((p) => (p || 0) + 1);

      requestAnimationFrame(() => {
        setIsRendering(false);
      });
    });
  };

  const deleteRow = (index) => {
    setRows((p = []) => p.filter((_, i) => i !== index));
    setRowCount((p) => Math.max(0, (p || 0) - 1));
  };
  const addRowBelow = (index) => {
    const fixedField = config?.fixedRows?.field;

    setRows((prev = []) => {
      const newRows = [...prev];

      const currentRow = newRows[index] || {};

      const newRow = fixedField
        ? {
            [fixedField]: currentRow?.[fixedField],
          }
        : {};

      newRows.splice(index + 1, 0, newRow);

      return newRows;
    });

    setRowCount((p) => p + 1);
  };
  // =====================
  // RENDER CELL
  // =====================
  const renderCell = (row, rowIndex, column) => {
    const fieldName = column.Field;

    const options = getFieldOptions(fieldName, row, config, masterData);

    return (
      <DynamicField
        column={column}
        fieldType={getFieldType(column, config)}
        value={row?.[fieldName] ?? ""}
        disabled={isFieldDisabled(fieldName, row, config)}
        required={isFieldRequired(fieldName, config)}
        readOnly={isFieldReadOnly(fieldName, config)}
        options={Array.isArray(options) ? options : []}
        onChange={(f, v) => handleChange(row.__originalIndex, f, v)}
      />
    );
  };
  const sortedRows = useMemo(() => {
    return sortRowsByConfig(rows, config).map((row) => ({
      ...row,
      __originalIndex: rows.indexOf(row),
    }));
  }, [rows, config]);
  // ============================
  // paste
  // ============================
  const handlePaste = useCallback(
    (e, rowIndex, columnIndex) => {
      const clipboardText = e.clipboardData?.getData("text/plain");

      if (!clipboardText) return;

      // Only intercept Excel/table paste
      if (!clipboardText.includes("\t") && !clipboardText.includes("\n")) {
        return;
      }

      e.preventDefault();

      setRows((prev = []) => {
        const updatedRows = pasteTableData({
          clipboardText,
          startRowIndex: rowIndex,
          startColumnIndex: columnIndex,
          rows: prev,
          visibleColumns,
          config,
        });

        setRowCount(updatedRows.length);

        return updatedRows;
      });
    },
    [setRows, setRowCount, visibleColumns, config],
  );
  // =====================
  // UI
  // =====================
  return (
    <div>
      {isRendering && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="text-center">
            <div
              className="spinner-border"
              role="status"
              style={{
                width: "3rem",
                height: "3rem",
              }}
            />
            <div className="mt-2">Loading rows...</div>
          </div>
        </div>
      )}
      {/* HEADER */}
      <div className="alert alert-info py-2">
        Total Rows: <strong>{rows.length}</strong>
      </div>

      {/* CONTROLS */}
      <div className="d-flex gap-2 mb-3 align-items-center">
        <input
          type="number"
          className="form-control"
          style={{ width: 120 }}
          value={rowCount}
          onChange={(e) => updateRowCount(Number(e.target.value) || 0)}
        />
        {[10, 25, 50].map((c) => (
          <button
            key={c}
            className="btn btn-outline-secondary"
            onClick={() => updateRowCount(c)}
          >
            {c}
          </button>
        ))}
        <input
          type="range"
          min={8}
          max={20}
          value={fontSize}
          onChange={(e) => setFontSize(Number(e.target.value))}
        />{" "}
        {fontSize} PX
      </div>

      {/* TABLE */}
      <div
        className="table-responsive"
        style={{
          maxHeight: "80vh",
          fontSize: `${fontSize}px`,
          overflow: "auto",
        }}
      >
        <table
          className="table table-bordered table-hover align-middle"
          style={{ padding: "0px", margin: "0px" }}
        >
          <thead className="table-dark sticky-top">
            <tr>
              <th style={{ width: 60 }}>#</th>

              {columnMeta.map(({ column, required }) => (
                <th key={column.Field}>
                  {column.Field}

                  {required && <span className=" ms-1">*</span>}
                </th>
              ))}

              <th style={{ width: 100 }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td>{rowIndex + 1}</td>

                {/* {columnMeta.map(({ column }) => (
                  <td
                    key={column.Field}
                    style={{ padding: "0px", margin: "0px" }}
                  >
                    {renderCell(row, rowIndex, column)}
                  </td>
                ))} */}
                {columnMeta.map(({ column }) => (
                  <td
                    key={column.Field}
                    style={{
                      padding: "0px",
                      margin: "0px",
                    }}
                    onPaste={(e) => {
                      const columnIndex = visibleColumns.findIndex(
                        (c) => c.Field === column.Field,
                      );

                      handlePaste(e, row.__originalIndex, columnIndex);
                    }}
                  >
                    {renderCell(row, rowIndex, column)}
                  </td>
                ))}
                <td className="" style={{ padding: "0px", margin: "0px" }}>
                  <div className="d-flex gap-1">
                    <button
                      style={{ height: "30px" }}
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => addRowBelow(row.__originalIndex)}
                    >
                      +
                    </button>

                    <button
                      style={{ height: "30px" }}
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteRow(row.__originalIndex)}
                    >
                      -
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ADD ROW */}
    </div>
  );
};

export default DynamicForm;
